import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconChevronRight } from '@tabler/icons-react'
import type { SchedulerApptStatus, SchedulerEvent } from './Scheduler.types'
import { SCHEDULER_APPT_STATUSES } from './Scheduler.types'
import { getNextSchedulerStatus } from './Scheduler.utils'

export type SchedulerContextMenuAction =
  | { type: 'status'; status: SchedulerApptStatus }
  | { type: 'goto'; target: string }
  | { type: 'info'; target: 'patient' | 'appt' }
  | { type: 'contact'; target: 'call' | 'message' | 'email' }
  | { type: 'referto' }
  | { type: 'notify' }
  | { type: 'addnote' }
  | { type: 'cancel' }

type SchedulerContextMenuProps = {
  event: SchedulerEvent
  top: number
  left: number
  darkMode?: boolean
  onClose: () => void
  onAction: (action: SchedulerContextMenuAction) => void
}

type SubmenuId = 'status' | 'goto' | 'info' | 'contact' | null

const STATUS_MENU_LABEL: Record<SchedulerApptStatus, string> = {
  Unconfirmed: 'Unconfirmed',
  Confirmed: 'Confirm',
  Late: 'Late',
  'No-show': 'No-show',
  'Check-in': 'Check-in',
  'In-treatment': 'In-treatment',
  'Check-out': 'Check-out',
  Complete: 'Complete',
}

const STATUS_ICON: Record<SchedulerApptStatus, string> = {
  Unconfirmed: 'mgc_question_fill',
  Confirmed: 'mgc_check_line',
  Late: 'mgc_time_fill',
  'No-show': 'mgc_close_circle_fill',
  'Check-in': 'mgc_user_follow_2_fill',
  'In-treatment': 'mgc_dental_fill',
  'Check-out': 'mgc_arrow_right_line',
  Complete: 'mgc_check_circle_fill',
}

const GOTO_ITEMS = [
  { id: 'treatmentplan', label: 'Treatment plan' },
  { id: 'periocharting', label: 'Perio charting' },
  { id: 'xrayimaging', label: 'X-ray imaging' },
  { id: 'laborder', label: 'Lab order' },
  { id: 'checkcoverage', label: 'Check coverage' },
  { id: 'reschedule', label: 'Reschedule' },
  { id: 'rqpreauth', label: 'Rq pre-auth' },
  { id: 'rqclaim', label: 'Rq claim' },
] as const

const INFO_ITEMS = [
  { id: 'patient' as const, label: 'Patient info' },
  { id: 'appt' as const, label: 'Appt. info' },
]

const CONTACT_ITEMS = [
  { id: 'call' as const, label: 'Call' },
  { id: 'message' as const, label: 'Message' },
  { id: 'email' as const, label: 'Email' },
]

const MenuIcon: React.FC<{ name: string; danger?: boolean }> = ({
  name,
  danger,
}) => (
  <i
    className={`${name} ds-scheduler__context-icon${
      danger ? ' ds-scheduler__context-icon--danger' : ''
    }`}
    aria-hidden="true"
  />
)

const SchedulerContextMenu: React.FC<SchedulerContextMenuProps> = ({
  event,
  top,
  left,
  darkMode = false,
  onClose,
  onAction,
}) => {
  const [openSubmenu, setOpenSubmenu] = useState<SubmenuId>(null)
  const [submenuSide, setSubmenuSide] = useState<'right' | 'left'>('right')

  const nextStatus = useMemo(
    () => getNextSchedulerStatus(event.status),
    [event.status],
  )

  useEffect(() => {
    const spaceRight = window.innerWidth - left - 200
    setSubmenuSide(spaceRight < 180 ? 'left' : 'right')
  }, [left])

  const run = (action: SchedulerContextMenuAction): void => {
    onAction(action)
    onClose()
  }

  const menuStyle: React.CSSProperties = {
    top: Math.min(top, window.innerHeight - 24),
    left: Math.min(left, window.innerWidth - 200),
  }

  return createPortal(
    <>
      <button
        type="button"
        className="ds-scheduler__context-backdrop"
        aria-label="Close menu"
        onClick={onClose}
        onContextMenu={(domEvent) => {
          domEvent.preventDefault()
          onClose()
        }}
      />
      <div
        className={`ds-scheduler__context-menu${
          darkMode ? ' ds-scheduler__context-menu--dark' : ''
        }`}
        style={menuStyle}
        role="menu"
        onMouseLeave={() => setOpenSubmenu(null)}
      >
        {nextStatus ? (
          <button
            type="button"
            className="ds-scheduler__context-item"
            role="menuitem"
            onClick={() => run({ type: 'status', status: nextStatus })}
          >
            <MenuIcon name={STATUS_ICON[nextStatus]} />
            <span>{STATUS_MENU_LABEL[nextStatus]}</span>
          </button>
        ) : null}

        <div
          className="ds-scheduler__context-item ds-scheduler__context-item--submenu"
          onMouseEnter={() => setOpenSubmenu('status')}
        >
          <button type="button" className="ds-scheduler__context-item-btn">
            <MenuIcon name="mgc_loading_2_line" />
            <span>Status</span>
            <IconChevronRight
              size={14}
              stroke={2}
              className="ds-scheduler__context-caret"
              aria-hidden="true"
            />
          </button>
          {openSubmenu === 'status' ? (
            <div
              className={`ds-scheduler__context-submenu ds-scheduler__context-submenu--${submenuSide}`}
              role="menu"
            >
              {SCHEDULER_APPT_STATUSES.map((status) => (
                <button
                  key={status}
                  type="button"
                  className={`ds-scheduler__context-item${
                    event.status === status
                      ? ' ds-scheduler__context-item--active'
                      : ''
                  }`}
                  role="menuitem"
                  onClick={() => run({ type: 'status', status })}
                >
                  <MenuIcon name={STATUS_ICON[status]} />
                  <span>{STATUS_MENU_LABEL[status]}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div
          className="ds-scheduler__context-item ds-scheduler__context-item--submenu"
          onMouseEnter={() => setOpenSubmenu('goto')}
        >
          <button type="button" className="ds-scheduler__context-item-btn">
            <MenuIcon name="mgc_arrow_up_line" />
            <span>Go to</span>
            <IconChevronRight
              size={14}
              stroke={2}
              className="ds-scheduler__context-caret"
              aria-hidden="true"
            />
          </button>
          {openSubmenu === 'goto' ? (
            <div
              className={`ds-scheduler__context-submenu ds-scheduler__context-submenu--${submenuSide}`}
              role="menu"
            >
              {GOTO_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="ds-scheduler__context-item"
                  role="menuitem"
                  onClick={() => run({ type: 'goto', target: item.id })}
                >
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div
          className="ds-scheduler__context-item ds-scheduler__context-item--submenu"
          onMouseEnter={() => setOpenSubmenu('info')}
        >
          <button type="button" className="ds-scheduler__context-item-btn">
            <MenuIcon name="mgc_eye_2_fill" />
            <span>View info</span>
            <IconChevronRight
              size={14}
              stroke={2}
              className="ds-scheduler__context-caret"
              aria-hidden="true"
            />
          </button>
          {openSubmenu === 'info' ? (
            <div
              className={`ds-scheduler__context-submenu ds-scheduler__context-submenu--${submenuSide}`}
              role="menu"
            >
              {INFO_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="ds-scheduler__context-item"
                  role="menuitem"
                  onClick={() => run({ type: 'info', target: item.id })}
                >
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div
          className="ds-scheduler__context-item ds-scheduler__context-item--submenu"
          onMouseEnter={() => setOpenSubmenu('contact')}
        >
          <button type="button" className="ds-scheduler__context-item-btn">
            <MenuIcon name="mgc_send_fill" />
            <span>Contact</span>
            <IconChevronRight
              size={14}
              stroke={2}
              className="ds-scheduler__context-caret"
              aria-hidden="true"
            />
          </button>
          {openSubmenu === 'contact' ? (
            <div
              className={`ds-scheduler__context-submenu ds-scheduler__context-submenu--${submenuSide}`}
              role="menu"
            >
              {CONTACT_ITEMS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="ds-scheduler__context-item"
                  role="menuitem"
                  onClick={() => run({ type: 'contact', target: item.id })}
                >
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <button
          type="button"
          className="ds-scheduler__context-item"
          role="menuitem"
          onMouseEnter={() => setOpenSubmenu(null)}
          onClick={() => run({ type: 'referto' })}
        >
          <MenuIcon name="mgc_department_fill" />
          <span>Refer to</span>
        </button>

        <button
          type="button"
          className="ds-scheduler__context-item"
          role="menuitem"
          onMouseEnter={() => setOpenSubmenu(null)}
          onClick={() => run({ type: 'notify' })}
        >
          <MenuIcon name="mgc_bell_ringing_fill" />
          <span>Notify</span>
        </button>

        <button
          type="button"
          className="ds-scheduler__context-item"
          role="menuitem"
          onMouseEnter={() => setOpenSubmenu(null)}
          onClick={() => run({ type: 'addnote' })}
        >
          <MenuIcon name="mgc_edit_4_fill" />
          <span>Add note</span>
        </button>

        <button
          type="button"
          className="ds-scheduler__context-item ds-scheduler__context-item--danger"
          role="menuitem"
          onMouseEnter={() => setOpenSubmenu(null)}
          onClick={() => run({ type: 'cancel' })}
        >
          <MenuIcon name="mgc_close_circle_fill" danger />
          <span>Cancel appt.</span>
        </button>
      </div>
    </>,
    document.body,
  )
}

export default SchedulerContextMenu
